import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertTriangle, CalendarPlus, Copy, Download, QrCode, RefreshCw, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { currentDevice, googleSubscribeUrl, ICSX5_STORE_URL } from '../constants/calendarSetup';

// ── Telegram Section ──────────────────────────────────────────────────────────

function TelegramSection() {
  const [status, setStatus] = useState(null); // { linked, bot_username }
  const [code, setCode] = useState(null);     // { code, expires_at }
  const [loading, setLoading] = useState(true);
  const [unlinking, setUnlinking] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await api.get('/profile/telegram/status');
      setStatus(res.data);
    } catch {
      toast.error('Failed to load Telegram status.');
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    try {
      const res = await api.post('/profile/telegram/generate-code');
      setCode(res.data);
    } catch {
      toast.error('Failed to generate code.');
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    if (status && !status.linked) generateCode();
  }, [status]); // eslint-disable-line

  const unlink = async () => {
    setUnlinking(true);
    try {
      await api.delete('/profile/telegram/unlink');
      toast.success('Telegram unlinked.');
      setCode(null);
      loadStatus();
    } catch {
      toast.error('Failed to unlink.');
    } finally {
      setUnlinking(false);
    }
  };

  const fmtExpiry = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div style={{ color: 'var(--text-muted)', padding: '12px 0' }}>Loading…</div>;
  }

  if (status?.linked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--success)' }} />
          <span style={{ fontWeight: 600, color: 'var(--success)' }}>Telegram connected</span>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={unlink} disabled={unlinking}>
          {unlinking ? 'Unlinking…' : 'Unlink'}
        </button>
      </div>
    );
  }

  const botName = status?.bot_username || 'the roster bot';

  return (
    <div>
      <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: 'var(--text-light)', lineHeight: 1.5 }}>
        Message our bot{status?.bot_username ? <> at <strong>@{status.bot_username}</strong></> : ''} on Telegram and enter the code below to link your account. You will then receive shift notifications via Telegram.
      </p>

      {code ? (
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            display: 'inline-block',
            fontFamily: 'monospace',
            fontSize: '2.2rem',
            fontWeight: 700,
            letterSpacing: '0.25em',
            color: 'var(--primary-dark)',
            background: 'var(--primary-xlight)',
            border: '2px solid var(--primary)',
            borderRadius: '10px',
            padding: '12px 24px',
            marginBottom: '8px',
          }}>
            {code.code}
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '4px 0 12px' }}>
            Code expires at {fmtExpiry(code.expires_at)}. Send this code to @{botName} in Telegram.
          </p>
          <button className="btn btn-secondary btn-sm" onClick={generateCode}>Refresh code</button>
        </div>
      ) : (
        <button className="btn btn-secondary btn-sm" onClick={generateCode}>Generate code</button>
      )}
    </div>
  );
}

// ── Calendar Section (personal iCalendar feed) ───────────────────────────────

const stepStyle = { margin: '0 0 4px', fontSize: '0.85rem', color: 'var(--text-light)', lineHeight: 1.5 };
const boxStyle = {
  background: 'var(--primary-xlight)', border: '1px solid var(--primary-light)',
  borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: '16px',
};
// Big tap target on phones
const bigButton = {
  width: '100%', minHeight: '48px', justifyContent: 'center', fontSize: '0.95rem', fontWeight: 600,
};

// Clipboard API, with a fallback for browsers / in-app views without it
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

// Amber hint for in-app browsers / installed app
function OpenInBrowserHint({ browser, onCopy }) {
  return (
    <div style={{
      display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap',
      padding: '10px 12px', marginBottom: '12px', borderRadius: 'var(--radius)',
      background: 'var(--warning-light)', border: '1px solid var(--warning)', fontSize: '0.86rem',
    }}>
      <AlertTriangle size={16} style={{ color: '#b45309', flexShrink: 0, marginTop: '2px' }} />
      <span style={{ flex: '1 1 180px' }}>
        <strong>Open this page in {browser}, then tap the button.</strong> It may not work in this window.
      </span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onCopy} style={{ minHeight: '36px' }}>
        <Copy size={14} /> Copy link
      </button>
    </div>
  );
}

function GoogleSteps() {
  return (
    <>
      <p style={stepStyle}>1. Tap the button and confirm <strong>Add</strong> (sign in to Google if asked).</p>
      <p style={stepStyle}>
        2. Open the <strong>Google Calendar app</strong> → <strong>Settings</strong> → the new calendar → turn on <strong>Sync</strong>.
      </p>
    </>
  );
}

// All options as before (computers, other apps, manual setup)
function ManualOptions({ info, onCopy }) {
  return (
    <>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <a className="btn btn-primary btn-sm" href={info.webcal_url}>
          <CalendarPlus size={14} /> Subscribe in Apple Calendar / Outlook
        </a>
        <a className="btn btn-secondary btn-sm" href={`${info.feed_url}?download=1`} download="leuchtstoff-shifts.ics">
          <Download size={14} /> Download .ics
        </a>
      </div>

      <div style={boxStyle}>
        <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: '0.9rem' }}>Add to Google Calendar</p>
        <p style={stepStyle}>1. Copy your calendar link.</p>
        <p style={stepStyle}>2. Open Google Calendar on a computer → <strong>Other calendars</strong> → <strong>+</strong> → <strong>From URL</strong>.</p>
        <p style={{ ...stepStyle, marginBottom: '12px' }}>3. Paste the link and click <strong>Add calendar</strong>.</p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input
            id="calendar-feed-url"
            type="text"
            className="form-input"
            readOnly
            value={info.feed_url}
            onFocus={(e) => e.target.select()}
            style={{ flex: '1 1 220px', minWidth: 0, fontFamily: 'monospace', fontSize: '0.78rem' }}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCopy}>
            <Copy size={14} /> Copy link
          </button>
        </div>
      </div>
    </>
  );
}

// Desktop: QR code of the webcal:// link, rendered locally, only on request
function PhoneQrBox({ webcalUrl }) {
  const [show, setShow] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!show || !canvasRef.current) return;
    let cancelled = false;
    import('qrcode')
      .then(({ default: QRCode }) => {
        if (!cancelled && canvasRef.current) {
          return QRCode.toCanvas(canvasRef.current, webcalUrl, { width: 200, margin: 1, errorCorrectionLevel: 'M' });
        }
        return null;
      })
      .catch(() => toast.error('Could not create the QR code.'));
    return () => { cancelled = true; };
  }, [show, webcalUrl]);

  return (
    <div style={boxStyle}>
      <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '0.9rem' }}>
        <Smartphone size={15} style={{ verticalAlign: '-2px', marginRight: '6px' }} />
        Set up on your phone
      </p>
      <p style={{ ...stepStyle, marginBottom: '10px' }}>
        Scan with your phone&apos;s camera (iPhone), or open this page on your phone for one-tap setup.
      </p>
      {!show ? (
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShow(true)}>
          <QrCode size={14} /> Show QR code
        </button>
      ) : (
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <canvas ref={canvasRef} width={200} height={200} style={{ background: '#fff', borderRadius: '6px', maxWidth: '100%' }} aria-label="QR code of your calendar link" />
          <div style={{ flex: '1 1 160px' }}>
            <p style={{ ...stepStyle, fontWeight: 600, color: 'var(--danger)' }}>This code is personal – don&apos;t share it.</p>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShow(false)}>Hide</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarSection({ focus }) {
  const [info, setInfo] = useState(null); // { feed_url, webcal_url, include_vacation }
  const [busy, setBusy] = useState(false);
  const device = useMemo(currentDevice, []);
  const loaded = !!info;

  useEffect(() => {
    api.get('/profile/calendar')
      .then((res) => setInfo(res.data))
      .catch(() => toast.error('Failed to load your calendar link.'));
  }, []);

  // /profile#calendar (publish notification): scroll here once the content is
  // in place and briefly highlight the card
  useEffect(() => {
    if (!focus || !loaded) return undefined;
    const card = document.getElementById('calendar');
    const t = setTimeout(() => {
      card?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (card) card.style.boxShadow = '0 0 0 3px var(--primary)';
    }, 300);
    const off = setTimeout(() => { if (card) card.style.boxShadow = ''; }, 2600);
    return () => { clearTimeout(t); clearTimeout(off); if (card) card.style.boxShadow = ''; };
  }, [focus, loaded]);

  const copyLink = async () => {
    if (await copyText(info.feed_url)) {
      toast.success('Link copied.');
      return;
    }
    const el = document.getElementById('calendar-feed-url');
    if (el) { el.focus(); el.select(); }
    toast('Copying is not possible here. Open "Other devices / manual" and copy the link by hand.');
  };

  const toggleVacation = async (include_vacation) => {
    setBusy(true);
    try {
      const res = await api.put('/profile/calendar/settings', { include_vacation });
      setInfo(res.data);
    } catch {
      toast.error('Failed to save.');
    } finally {
      setBusy(false);
    }
  };

  const resetLink = async () => {
    if (!window.confirm('Reset your calendar link?\n\nThe old link stops working immediately. Calendars subscribed with it will stop updating, and you will need to subscribe again with the new link.')) return;
    setBusy(true);
    try {
      const res = await api.post('/profile/calendar/reset');
      setInfo(res.data);
      toast.success('New calendar link created. Subscribe again with the new link.');
    } catch {
      toast.error('Failed to reset the link.');
    } finally {
      setBusy(false);
    }
  };

  if (!info) return <div style={{ color: 'var(--text-muted)', padding: '12px 0' }}>Loading…</div>;

  const googleUrl = googleSubscribeUrl(info.webcal_url);
  const mobile = device.os !== 'desktop';

  return (
    <div>
      {device.os === 'ios' && (
        <>
          {(device.inApp || device.standalone) && <OpenInBrowserHint browser="Safari" onCopy={copyLink} />}
          <a className="btn btn-primary" href={info.webcal_url} style={bigButton}>
            <CalendarPlus size={18} /> Add to Apple Calendar
          </a>
          <p style={{ ...stepStyle, margin: '6px 0 16px', color: 'var(--text-muted)' }}>
            Nothing happens? Open this page in Safari and try again.
          </p>
          <div style={boxStyle}>
            <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: '0.9rem' }}>Using Google Calendar on your iPhone?</p>
            <p style={{ ...stepStyle, marginBottom: '10px' }}>
              The Apple subscription does not show up in the Google Calendar app. Add it to Google instead:
            </p>
            <a className="btn btn-secondary btn-sm" href={googleUrl} target="_blank" rel="noopener noreferrer" style={{ marginBottom: '10px' }}>
              <CalendarPlus size={14} /> Add to Google Calendar
            </a>
            <GoogleSteps />
          </div>
        </>
      )}

      {device.os === 'android' && (
        <>
          {(device.inApp || device.standalone) && <OpenInBrowserHint browser="Chrome" onCopy={copyLink} />}
          <a className="btn btn-primary" href={googleUrl} target="_blank" rel="noopener noreferrer" style={bigButton}>
            <CalendarPlus size={18} /> Add to Google Calendar
          </a>
          <div style={{ margin: '10px 0 16px' }}>
            <GoogleSteps />
            <p style={{ ...stepStyle, color: 'var(--text-muted)' }}>
              If the Calendar app opens instead of a page with &quot;Add&quot;, open the link in Chrome and choose <strong>Desktop site</strong>.
            </p>
          </div>
          <div style={boxStyle}>
            <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '0.9rem' }}>
              ICSx⁵ (free app, recommended if Google does not work)
            </p>
            <p style={{ ...stepStyle, marginBottom: '10px' }}>
              Puts your shifts into your phone&apos;s calendar and keeps them updated.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <a className="btn btn-secondary btn-sm" href={info.webcal_url}>
                <CalendarPlus size={14} /> Open in ICSx⁵
              </a>
              <a className="btn btn-secondary btn-sm" href={ICSX5_STORE_URL} target="_blank" rel="noopener noreferrer">
                <Download size={14} /> Get ICSx⁵
              </a>
              <button type="button" className="btn btn-secondary btn-sm" onClick={copyLink}>
                <Copy size={14} /> Copy link
              </button>
            </div>
          </div>
        </>
      )}

      {mobile ? (
        <details style={{ marginBottom: '16px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: 'var(--primary-dark)', padding: '6px 0', minHeight: '32px' }}>
            Other devices / manual
          </summary>
          <div style={{ marginTop: '10px' }}>
            <ManualOptions info={info} onCopy={copyLink} />
          </div>
        </details>
      ) : (
        <>
          <ManualOptions info={info} onCopy={copyLink} />
          <PhoneQrBox webcalUrl={info.webcal_url} />
        </>
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '0.9rem', marginBottom: '14px' }}>
        <input
          type="checkbox"
          checked={info.include_vacation}
          disabled={busy}
          onChange={(e) => toggleVacation(e.target.checked)}
          style={{ width: '16px', height: '16px', accentColor: 'var(--primary-dark)' }}
        />
        Include my approved vacations (all-day events)
      </label>

      <p style={{ margin: '0 0 6px', fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Google updates subscribed calendars only every few hours. For last-minute changes, check the app.
      </p>
      <p style={{ margin: '0 0 14px', fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Keep this link private: anyone with it can see your shifts.
      </p>

      <button className="btn btn-secondary btn-sm" onClick={resetLink} disabled={busy}>
        <RefreshCw size={14} /> Reset link
      </button>
    </div>
  );
}

// ── Contract Details Section ──────────────────────────────────────────────────

function ContractDetailsSection() {
  const { user } = useAuth();

  const fmtVal = (val) =>
    val != null && val !== 0 && val !== '' ? Number(val) : '—';

  return (
    <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Contract Details</h3>
      <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
        Set by your admin. Contact them to make changes.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Contracted hours/week</label>
          <input
            type="text"
            className="form-input"
            value={fmtVal(user?.contracted_hours_per_week)}
            disabled
            style={{ opacity: 0.7 }}
          />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Preferred shifts/week</label>
          <input
            type="text"
            className="form-input"
            value={fmtVal(user?.preferred_shifts_per_week)}
            disabled
            style={{ opacity: 0.7 }}
          />
        </div>
      </div>
    </div>
  );
}

// ── OM Roles Badges ───────────────────────────────────────────────────────────

function OMRolesBadges() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    api.get('/om/roles')
      .then((res) => setRoles((res.data || []).filter((r) => r.is_active && String(r.holder_user_id) === String(user?.id))))
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line

  if (!roles.length) return null;

  return (
    <div className="card" style={{ padding: '16px 20px', marginBottom: '16px' }}>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginRight: '8px' }}>Ämter:</span>
      {roles.map((r) => (
        <span key={r.id} className="badge badge-info" style={{ marginRight: '6px' }}>{r.name}</span>
      ))}
    </div>
  );
}

// ── Profile Info Section ──────────────────────────────────────────────────────

function ProfileInfoSection() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [changingPw, setChangingPw] = useState(false);

  const saveName = async () => {
    if (!name.trim()) { toast.error('Name cannot be empty.'); return; }
    setSaving(true);
    try {
      const res = await api.put('/auth/me', { name: name.trim() });
      updateUser({ name: res.data.name });
      toast.success('Name updated.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update name.');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error('All fields required.'); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error('New passwords do not match.'); return; }
    if (pwForm.new_password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setChangingPw(true);
    try {
      await api.put('/auth/me/password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Password changed.');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change password.');
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <>
      <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem' }}>Profile Info</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Display Name</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Email</label>
            <input type="email" className="form-input" value={user?.email || ''} disabled style={{ opacity: 0.6 }} />
          </div>
        </div>

        <button className="btn btn-primary btn-sm" onClick={saveName} disabled={saving}>
          {saving ? 'Saving…' : 'Save Name'}
        </button>
      </div>

      <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem' }}>Change Password</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Current Password</label>
            <input
              type="password"
              className="form-input"
              value={pwForm.current_password}
              onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">New Password</label>
            <input
              type="password"
              className="form-input"
              value={pwForm.new_password}
              onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Confirm New Password</label>
            <input
              type="password"
              className="form-input"
              value={pwForm.confirm}
              onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
            />
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={changePassword} disabled={changingPw}>
          {changingPw ? 'Changing…' : 'Change Password'}
        </button>
      </div>
    </>
  );
}

// ── AbsencesSection (read-only for current user) ──────────────────────────────

function UserAbsencesSection({ userId }) {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    api.get(`/users/${userId}/absences`)
      .then((res) => setAbsences(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Scheduled Absences</h3>
      <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
        Set by your admin. Contact them to make changes.
      </p>
      {loading ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading…</p>
      ) : absences.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No absences recorded.</p>
      ) : (
        <div>
          {absences.map((a) => (
            <div
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 0',
                borderBottom: '1px solid var(--border)',
                fontSize: '0.875rem',
              }}
            >
              <span style={{ fontWeight: 500, minWidth: '120px' }}>
                {a.start_date === a.end_date ? a.start_date : `${a.start_date} – ${a.end_date}`}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem', textTransform: 'capitalize' }}>
                {a.absence_type.replace(/_/g, ' ')}
              </span>
              {a.note && (
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.82rem' }}>
                  {a.note}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ProfilePage ───────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    api.get('/auth/me').then((res) => updateUser(res.data)).catch(() => {});
  }, []); // eslint-disable-line

  // Deep links such as /profile#telegram-section (#calendar scrolls itself once loaded)
  useEffect(() => {
    if (!location.hash || location.hash === '#calendar') return;
    const t = setTimeout(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 className="page-title">Profile</h1>
          <p className="page-subtitle">Manage your account and notification settings</p>
        </div>
      </div>

      <ProfileInfoSection />

      <ContractDetailsSection />

      <OMRolesBadges />

      <UserAbsencesSection userId={user?.id} />

      <div id="calendar" className="card" style={{ padding: '20px', marginBottom: '16px', scrollMarginTop: '80px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Calendar</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
          See your shifts in your own calendar app (Google, Apple, Outlook). Swaps, covers and changes update automatically.
        </p>
        <CalendarSection focus={location.hash === '#calendar'} />
      </div>

      <div id="telegram-section" className="card" style={{ padding: '20px' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>Telegram</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: 'var(--text-light)' }}>
          Link your Telegram account to receive shift notifications.
        </p>
        <TelegramSection />
      </div>
    </div>
  );
}
